import React, { useState, useEffect, useRef } from 'react'; // Added hooks
import { FaUtensils, FaConciergeBell, FaLeaf } from 'react-icons/fa';

const features = [
  {
    icon: <FaUtensils />,
    title: 'Premium Dining',
    description: 'It\'s very personal, and can only be a positive experience.'
  },
  {
    icon: <FaConciergeBell />,
    title: 'Abundant Flavors',
    description: 'At secret recipe, we take immense pride in crafting.'
  },
  {
    icon: <FaLeaf />,
    title: 'Indigenous Meal',
    description: 'With local ingredients, unique spins on traditional flavors.'
  }
];

const FeatureBar = () => {
  const [inView, setInView] = useState(false); // State to track section visibility
  const sectionRef = useRef(null); // Ref for the section
  const [animationKey, setAnimationKey] = useState(0); // Key to force re-animation

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setInView(true);
            setAnimationKey(prevKey => prevKey + 1); // Increment key to re-trigger animations
          } else {
            setInView(false);
          }
        });
      },
      {
        threshold: 0.1, // Trigger when 10% of the section is visible
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const featureBarStyle = {
    backgroundColor: '#111',
    color: '#fff',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: '20px',
    padding: '60px 5%',
    margin: '0',
  };

  const featureItemStyle = {
    flex: '1 1 280px',
    maxWidth: '300px',
    borderRadius: '12px',
    padding: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    backgroundColor: 'transparent',
    transition: 'transform 0.4s ease, box-shadow 0.4s ease',
    position: 'relative',
    overflow: 'hidden',
    cursor: 'pointer',
    // Initial state for item animation
    opacity: 0,
    transform: 'translateY(50px)',
    transition: 'opacity 0.6s ease-out, transform 0.6s ease-out',
  };

  const iconStyle = {
    width: '60px',
    height: '60px',
    border: '2px solid #fff',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '28px',
    flexShrink: 0,
    transition: 'transform 0.4s ease, border-color 0.4s ease, color 0.4s ease',
    zIndex: 2,
  };

  const textContentStyle = { // <-- This is the definition that was missing or had a typo
    flex: 1,
    zIndex: 2,
  };

  // Function to get animated styles for text elements
  const getAnimatedTextStyle = (baseStyle, delay) => ({
    ...baseStyle,
    opacity: inView ? 1 : 0,
    transform: inView ? 'translateY(0)' : 'translateY(20px)',
    transition: `opacity 0.6s ease-out ${delay}s, transform 0.6s ease-out ${delay}s`,
  });

  const baseTitleStyle = {
    fontSize: '16px',
    fontWeight: '600',
    marginBottom: '6px',
    fontFamily: 'Playfair Display',
    color: '#fff',
    transition: 'color 0.4s ease',
  };

  const baseDescriptionStyle = {
    fontSize: '14px',
    color: '#ccc',
    lineHeight: 1.5,
    transition: 'color 0.4s ease',
  };

  return (
    <>
      <style>
        {`
          .feature-item-hover:hover {
            transform: translateY(-10px);
            box-shadow: 0 10px 20px rgba(0, 0, 0, 0.4);
          }

          .feature-item-hover:hover .feature-icon {
            transform: scaleX(-1) rotate(360deg);
            border-color: #D6A54F;
            color: #D6A54F;
            background-color: transparent;
          }

          .feature-item-hover:hover .feature-title {
            color: #111;
          }
          .feature-item-hover:hover .feature-description {
            color: #fff;
          }

          /* --- Responsive adjustments --- */
          @media (max-width: 768px) {
            .feature-bar-section {
              width: 100%;
              padding: 0;
              margin: 0;
              gap: 0;
              box-sizing: border-box;
            }

            .feature-item-style {
              max-width: 100%;
              flex-basis: 100%;
              border-bottom: 1px solid rgba(255, 255, 255, 0.1);
              border-radius: 0;
            }

            .feature-item-style:last-child {
              border-bottom: none;
            }
          }

          @media (min-width: 769px) {
            .feature-bar-section {
              gap: 40px;
              padding: 60px 5%;
              width: auto;
              margin: 0;
            }
            .feature-item-style {
              max-width: 300px;
              flex-basis: 280px;
              border-bottom: none;
              border-radius: 12px;
            }
          }
        `}
      </style>

      {/* Attach sectionRef to the main section */}
      <section style={featureBarStyle} className="feature-bar-section" ref={sectionRef}>
        {features.map((feature, index) => (
          <div
            key={`${animationKey}-${index}`} // Key for re-animation of each item
            style={{
              ...featureItemStyle,
              // Apply animation based on inView and stagger
              opacity: inView ? 1 : 0,
              transform: inView ? 'translateY(0)' : 'translateY(50px)',
              transitionDelay: `${index * 0.1}s`, // Stagger each item's animation
            }}
            className="feature-item-hover feature-item-style"
          >
            <div style={iconStyle} className="feature-icon">{feature.icon}</div>
            <div style={textContentStyle}>
              {/* Apply animated styles to title and description */}
              <h3
                style={getAnimatedTextStyle(baseTitleStyle, 0.2 + index * 0.1)}
                key={`title-${animationKey}-${index}`} // Key for re-animation
                className="feature-title"
              >
                {feature.title}
              </h3>
              <p
                style={getAnimatedTextStyle(baseDescriptionStyle, 0.3 + index * 0.1)}
                key={`description-${animationKey}-${index}`} // Key for re-animation
                className="feature-description"
              >
                {feature.description}
              </p>
            </div>
          </div>
        ))}
      </section>
    </>
  );
};

export default FeatureBar;